import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar as CalendarIcon, Clock, Users } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';


interface CalendarProps {
  isGlobalDarkMode: boolean;
  isMobile?: boolean;
}

const Calendar: React.FC<CalendarProps> = ({ isGlobalDarkMode, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = React.useState('');

  const handleMobileMenuToggle = () => {
    // Top-level item without sub-items, menu toggle does nothing for now
  };

  const handleMobileAdd = () => {
    alert('Create new event (coming soon)');
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Calendar | AgencyPlus</title>
        <meta name="description" content="Manage your schedule and events" />
        <link rel="canonical" href="/boards/calendar" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={handleMobileAdd}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search events..."
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-foreground">Calendar</h1>
            <p className="text-muted-foreground">
              Manage your schedule and events
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-workspace-accent/15">
            <CalendarIcon className="w-12 h-12 text-workspace-accent" />
          </div>

          <h2 className="text-2xl font-bold mb-4 text-foreground">
            Coming Soon
          </h2>

          <p className="text-center max-w-md mb-8 text-muted-foreground">
            We're working on an amazing calendar experience to help you manage your schedule, events, and deadlines all in one place.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-green-500/15">
                <Clock className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Event Scheduling
              </h3>
              <p className="text-sm text-muted-foreground">
                Schedule meetings, deadlines, and important events
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-violet-500/15">
                <Users className="w-6 h-6 text-violet-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Team Collaboration
              </h3>
              <p className="text-sm text-muted-foreground">
                Share calendars and coordinate with team members
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-orange-500/15">
                <CalendarIcon className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Multiple Views
              </h3>
              <p className="text-sm text-muted-foreground">
                Month, week, and day views for better planning
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
